import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/shared/hooks/useAuth';
import { absoluteAvatarUrl, UserAvatar } from '@/entities/user';
import { cn } from '@/shared/lib/cn';
import { KnowMatchMark } from '@/shared/ui';
import { ROUTES } from '@/shared/config/constants';

export type AppHeaderProps = {
  title?: string;
};

export const AppHeader = ({ title }: AppHeaderProps) => {
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Inclui fotoUpdatedAt para cache-bust apos upload local; sem isso o
  // browser reusa a versao em cache da URL estavel e o header continua
  // mostrando as iniciais antigas.
  const avatar = absoluteAvatarUrl(user?.fotoUrl, user?.fotoUpdatedAt ?? null);

  return (
    <header
      className={cn(
        'sticky top-0 z-20 flex h-16 items-center justify-between bg-background/80 px-4 backdrop-blur-md transition-shadow duration-200 supports-[backdrop-filter]:bg-background/70 lg:px-8',
        // Sem borda dura: sombra muito suave so aparece quando rola.
        scrolled
          ? 'shadow-[0_1px_0_rgba(16,24,40,0.06),0_8px_24px_-12px_rgba(16,24,40,0.12)]'
          : 'shadow-none',
      )}
    >
      <div className="flex items-center gap-3">
        {title ? (
          <h1 className="text-base font-semibold tracking-tight text-foreground">{title}</h1>
        ) : (
          <div className="flex items-center gap-2 lg:hidden">
            <KnowMatchMark className="h-7 w-auto text-foreground" />
            <span className="text-sm font-semibold tracking-tight">Match</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Notificacoes ocultadas temporariamente. */}
        {user ? (
          <Link
            to={ROUTES.APP.PROFILE}
            aria-label="Meu perfil"
            // inline-flex + shrink-0 garante que o <a> tenha exatamente as
            // dimensoes quadradas do UserAvatar; sem isso o default
            // display:inline do <a> aplica baseline alignment e o
            // ring-1/rounded-full ficam ovais, dando impressao de foto
            // achatada mesmo com object-cover correto.
            className="inline-flex shrink-0 rounded-full ring-1 ring-border transition-transform hover:scale-105 lg:hidden"
          >
            <UserAvatar nome={user.nome} fotoUrl={avatar} size="sm" />
          </Link>
        ) : null}
      </div>
    </header>
  );
};
